import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Flex,
  SimpleGrid,
  useColorModeValue,
  Spinner,
  Center,
  Button,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowBackIcon } from "@chakra-ui/icons";
import CardTransactionsTable from "views/admin/dataTables/components/CardTransactionsTable";
import useEndpoints from "providers/Endpoints";
import constants from "providers/Constants";

export default function CardTransactions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cardId } = useParams();
  const endpoints = useEndpoints();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const bgColor = useColorModeValue("white", "gray.800");

  useEffect(() => {
    if (cardId) {
      getCardTransactions(cardId, 1);
    } else {
      setLoading(false);
    }
  }, [cardId]);

  const getCardTransactions = useCallback((cId, page = 1) => {
    setLoading(true);
    endpoints.getCardTransactions(cId, page, (error, data) => {
      setLoading(false);
      if (error) {
        constants.showToast(
          "error",
          t("FAILED"),
          error?.message || t("Snap! Something went wrong, Try again")
        );
        setTransactions([]);
      } else {
        setTransactions(data || []);
      }
    });
  }, [endpoints, t]);

  const handleGoBack = useCallback(() => {
    navigate("/admin/cards");
  }, [navigate]);

  if (!cardId) {
    return (
      <Box pt={{ base: "180px", md: "80px", xl: "80px" }} px="20px">
        <Flex align="center" gap={4} mb="30px">
          <Button
            leftIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            variant="ghost"
            colorScheme="brand"
          >
            {t("Back to Cards")}
          </Button>
          <Text fontSize="2xl" fontWeight="700" color={textColor}>
            {t("Card Transactions")}
          </Text>
        </Flex>
        <Center height="200px">
          <Box>
            <Text color={textColor} fontSize="sm" fontWeight="500">
              {t("No card selected. Please select a card from the payment cards page.")}
            </Text>
          </Box>
        </Center>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "180px", md: "80px", xl: "80px" }} px="20px" pb="40px">
      {/* Header Section */}
      <Flex align="center" gap={4} mb="30px">
        <Button
          leftIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
          variant="outline"
          colorScheme="brand"
          size="sm"
        >
          {t("Back")}
        </Button>
      </Flex>

      {/* Main Content */}
      <SimpleGrid mb="20px" columns={{ sm: 1, md: 1 }} spacing={{ base: "20px", xl: "20px" }}>
        {loading ? (
          <Flex justifyContent="center" alignItems="center" height="50vh">
            <Spinner size="xl" />
          </Flex>
        ) : (
          <Box bg={bgColor} borderRadius="16px">
            <CardTransactionsTable
              sortArray={[5, 10, 20, 50]}
              index={() => {}}
              tableData={transactions}
            />
          </Box>
        )}
      </SimpleGrid>
    </Box>
  );
}
